import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, ChevronDown } from "lucide-react";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";
import { getStaffToken, ApiError } from "@/lib/api";
import { subscribeNotification } from "@/lib/notification-ws";
import { useNotificationContext } from "@/contexts/useNotificationContext";
import { canAcceptQrOrders, canManageQrOrders } from "@/rbac/actionPermissions";
import {
  QR_PENDING_ORDERS_KEY,
  QR_PENDING_POLL_MS,
  QR_PENDING_POLL_NO_SOCKET_MS,
  useQRPendingOrders,
} from "@/hooks/useQRPendingOrders";
import { useStaffConnectionKey } from "@/lib/staffConnection";
import { useToast } from "@/hooks/use-toast";
import {
  ACCEPT_UNKNOWN_MESSAGE,
  acceptErrorMessage,
  alreadyResolved,
  isNetworkError,
  myProfileId,
  ORDER_REMOVED_LABEL,
  remainingMs,
  resolvedLabel,
  secondsLeft,
  skewFrom,
} from "@/lib/qrAccept";
import { acceptedToast, unconfirmedAcceptToast } from "@/lib/qrAcceptPrint";
import { playQrDing } from "@/lib/qrDing";
import { getPendingOrderId } from "@/types/qr-orders";
import type {
  QROrderFollowUpEventMeta,
  QROrderPendingEventMeta,
  QROrderResolvedEventMeta,
} from "@/types/qr-orders";
import type { WsNotificationEvent } from "@/types/notification";
import { QRAcceptPromptCard } from "./QRAcceptPromptCard";
import { QRPendingOrdersPanel } from "./QRPendingOrdersPanel";
import { useAcceptAndPrint } from "./useAcceptAndPrint";
import {
  ACCEPT_GIVE_UP_MS,
  ATTENTION_MAX_AGE_MS,
  GIVE_UP_AFTER_MS,
  isPromptScreen,
  promptFromEvent,
  promptFromOrder,
  promptsReducer,
  RECHECK_AFTER_MS,
  RESOLVED_LINGER_MS,
  tableLabel,
  TICK_MS,
  type AcceptPrompt,
} from "./qrAcceptPromptState";
import { useQRPendingPanelOpen } from "@/lib/qrPendingPanelOpen";

/**
 * The QR Accept prompts, on every staff screen for qr_orders.accept or
 * qr_orders.manage holders. A guest's order shows here as soon as it is
 * placed (the qr_order_pending event, or the pending list on load and on
 * reconnect); whoever accepts it gets the table and its KOT prints here.
 */
export function QRAcceptPrompts() {
  const { pathname } = useLocation();
  // Re-read on sign-in and on a staff / outlet switch; a new person starts with
  // no prompts (the host is remounted), and gets theirs from the list and events.
  const connectionKey = useStaffConnectionKey();
  if (!connectionKey || !getStaffToken() || !canAcceptQrOrders() || !isPromptScreen(pathname)) return null;
  return <QRAcceptPromptsHost key={connectionKey} />;
}

/** Device time, every TICK_MS while `active` (fresh before paint when it turns on). */
function useTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useLayoutEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

const isForbiddenError = (error: unknown) => error instanceof ApiError && error.status === 403;

const NOT_TEXT_INPUTS = new Set(["button", "checkbox", "radio", "submit", "reset", "range", "color", "file", "image"]);
/** A field that opens the on-screen keyboard when focused. */
const isTextField = (el: HTMLElement): boolean =>
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLInputElement && !NOT_TEXT_INPUTS.has(el.type)) ||
  el.isContentEditable;

function QRAcceptPromptsHost() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { wsStatus } = useNotificationContext();
  const { acceptAndPrint } = useAcceptAndPrint();
  const canReview = canManageQrOrders();

  // The server says this person can't accept (e.g. signed in before they were
  // given qr_orders.accept): no prompts, no ding, no polling, no ticking.
  const [forbiddenState, setForbidden] = useState(false);
  // The list backs up the socket: every 10 s while it is down (inside the 20 s window), 30 s while it is open.
  const { data: list, error: listError } = useQRPendingOrders("pending", {
    enabled: !forbiddenState,
    refetchInterval: wsStatus === "connected" ? QR_PENDING_POLL_MS : QR_PENDING_POLL_NO_SOCKET_MS,
  });
  const forbidden = forbiddenState || isForbiddenError(listError);
  const forbiddenRef = useRef(forbidden);
  forbiddenRef.current = forbidden;
  useEffect(() => {
    if (isForbiddenError(listError)) setForbidden(true);
  }, [listError]);

  const [prompts, dispatch] = useReducer(promptsReducer, []);
  const [minimized, setMinimized] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [skewMs, setSkewMs] = useState(0);
  const [announcement, setAnnouncement] = useState("");

  const promptsRef = useRef(prompts);
  promptsRef.current = prompts;
  /** Orders this device is done with: never prompt for them again. */
  const done = useRef(new Set<string>());
  /** Whether the skew came from the pending list (better than an event's created_at). */
  const skewFromList = useRef(false);
  const skewRef = useRef(0);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Focus: after an Accept the button goes away; focus moves to the next
  // card's Accept, else back to where it was before the prompts.
  const regionRef = useRef<HTMLDivElement>(null);
  const lastOutsideFocus = useRef<HTMLElement | null>(null);
  /** Set when an Accept's button went away with focus on it: how that Accept was pressed. */
  const refocus = useRef<{ viaKeyboard: boolean } | null>(null);
  /**
   * Accepts started from a focused prompt (the button is disabled meanwhile, so
   * focus may drop to the page), and whether they were pressed from the keyboard.
   */
  const acceptsHoldingFocus = useRef(new Map<string, boolean>());
  /** The last input: a tap or click ("pointer") or a key. */
  const lastInput = useRef<"pointer" | "keyboard">("pointer");
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && target !== document.body && !regionRef.current?.contains(target)) {
        lastOutsideFocus.current = target;
      }
    };
    const onPointer = () => {
      lastInput.current = "pointer";
    };
    const onKey = () => {
      lastInput.current = "keyboard";
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, []);
  const focusIsInPrompts = useCallback(
    () => !!regionRef.current && regionRef.current.contains(document.activeElement),
    [],
  );
  /** This Accept took focus with it: put focus somewhere sensible once its button is gone. */
  const refocusAfter = useCallback(
    (id: string) => {
      const viaKeyboard = acceptsHoldingFocus.current.get(id);
      acceptsHoldingFocus.current.delete(id);
      // Only when focus is still where the Accept left it (not moved to an input meanwhile).
      if (viaKeyboard !== undefined && (document.activeElement === document.body || focusIsInPrompts())) {
        refocus.current = { viaKeyboard };
      }
    },
    [focusIsInPrompts],
  );
  useEffect(() => {
    const pending = refocus.current;
    if (!pending) return;
    refocus.current = null;
    if (regionRef.current?.contains(document.activeElement) && document.activeElement !== document.body) {
      const active = document.activeElement as HTMLButtonElement;
      if (!active.disabled) return;
    }
    const next = regionRef.current?.querySelector<HTMLButtonElement>("[data-qr-accept]:not([disabled])");
    if (next) {
      next.focus();
      return;
    }
    const back = lastOutsideFocus.current;
    if (!back?.isConnected) return;
    // Back into a text field only for keyboard users: on a phone it would pop the keyboard up.
    if (isTextField(back) && !pending.viaKeyboard) return;
    back.focus();
  }, [prompts]);

  const ticking = !forbidden && prompts.some((p) => p.phase === "open" || p.phase === "accepting");
  const now = useTicker(ticking);

  const updateSkew = useCallback((value: number | null, fromList: boolean) => {
    if (value === null) return;
    if (!fromList && skewFromList.current) return;
    if (fromList) skewFromList.current = true;
    skewRef.current = value;
    setSkewMs(value);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => clearTimeout(t));
      pending.clear();
    };
  }, []);

  const remove = useCallback((id: string) => {
    done.current.add(id);
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    dispatch({ type: "remove", id });
  }, []);

  /** Say who resolved it, then let it go. */
  const resolve = useCallback((id: string, outcome: string) => {
    done.current.add(id);
    dispatch({ type: "update", id, patch: { phase: "resolved", outcome, error: null } });
    const prompt = promptsRef.current.find((p) => p.id === id);
    if (prompt) setAnnouncement(`${tableLabel(prompt)}: ${outcome}`);
    if (!timers.current.has(id)) {
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          dispatch({ type: "remove", id });
        }, RESOLVED_LINGER_MS),
      );
    }
  }, []);

  const show = useCallback((prompt: AcceptPrompt, ding: boolean) => {
    if (forbiddenRef.current) return;
    if (done.current.has(prompt.id) || promptsRef.current.some((p) => p.id === prompt.id)) return;
    promptsRef.current = [...promptsRef.current, prompt];
    dispatch({ type: "add", prompt });
    if (prompt.phase === "open") {
      setMinimized(false);
      setAnnouncement(`New QR order, ${tableLabel(prompt)}`);
      if (ding) playQrDing();
    }
  }, []);

  // ---- realtime ----
  useEffect(() => {
    const onPending = (payload: unknown) => {
      const event = payload as WsNotificationEvent;
      const receivedAt = Date.now();
      updateSkew(skewFrom(event.created_at, receivedAt), false);
      const prompt = promptFromEvent(
        (event.metadata ?? {}) as unknown as QROrderPendingEventMeta,
        event.created_at,
        receivedAt,
      );
      if (!prompt) return;
      if (remainingMs(prompt.deadlineAt, receivedAt, skewRef.current) <= 0) return;
      show(prompt, true);
    };
    const onResolved = (payload: unknown) => {
      const meta = ((payload as WsNotificationEvent).metadata ?? {}) as unknown as QROrderResolvedEventMeta;
      const id = meta.pending_order_id;
      if (!id) return;
      done.current.add(id);
      const prompt = promptsRef.current.find((p) => p.id === id);
      if (!prompt || prompt.phase === "resolved") return;
      const me = myProfileId();
      const mine = !!meta.resolved_by_id && meta.resolved_by_id === me;
      if (mine) {
        // This person accepted it (the panel, another device, or this prompt, whose answer says so).
        if (prompt.phase === "accepting") return;
        if (prompt.unconfirmedAt != null) toast(unconfirmedAcceptToast(tableLabel(prompt)));
        remove(id);
        return;
      }
      resolve(id, resolvedLabel(meta, me));
    };
    const onNeedsAttention = (payload: unknown) => {
      const meta = ((payload as WsNotificationEvent).metadata ?? {}) as unknown as QROrderFollowUpEventMeta;
      const prompt = promptsRef.current.find((p) => p.id === meta.pending_order_id);
      if (!prompt || prompt.phase === "resolved") return;
      dispatch({
        type: "update",
        id: prompt.id,
        patch:
          prompt.phase === "accepting"
            ? { resumePhase: "attention", reason: meta.reason ?? null }
            : { phase: "attention", reason: meta.reason ?? null },
      });
    };
    const unsubs = [
      subscribeNotification("qr_order_pending", onPending),
      subscribeNotification("qr_order_resolved", onResolved),
      subscribeNotification("qr_order_needs_attention", onNeedsAttention),
    ];
    return () => unsubs.forEach((u) => u());
  }, [remove, resolve, show, updateSkew, toast]);

  // ---- the pending list: restores prompts after a reload or a reconnect ----
  useEffect(() => {
    if (!list) return;
    updateSkew(list.skewMs ?? null, true);
    const skew = list.skewMs ?? skewRef.current;
    const deviceNow = Date.now();
    const stillPending = new Set<string>();
    for (const order of list.items) {
      if (order.status && String(order.status).toLowerCase() !== "pending") continue;
      const id = getPendingOrderId(order);
      if (!id) continue;
      stillPending.add(id);
      const existing = promptsRef.current.find((p) => p.id === id);
      if (existing) {
        if (order.auto_accept_error && existing.phase === "open") {
          dispatch({ type: "update", id, patch: { phase: "attention", reason: order.auto_accept_error } });
        }
        continue;
      }
      const prompt = promptFromOrder(order, id, deviceNow);
      if (!prompt) continue;
      // Counting down, or waiting for staff after a failed auto-accept (but not one left for half an hour).
      if (prompt.phase === "open" && remainingMs(prompt.deadlineAt, deviceNow, skew) <= 0) continue;
      if (
        prompt.phase === "attention" &&
        prompt.submittedAt != null &&
        deviceNow + skew - prompt.submittedAt > ATTENTION_MAX_AGE_MS
      ) {
        continue;
      }
      show(prompt, prompt.phase === "open");
    }
    // Resolved while this device missed the event: a list asked for after the
    // prompt appeared no longer has it.
    const askedAt = list.requestedAt ?? 0;
    for (const prompt of promptsRef.current) {
      if (stillPending.has(prompt.id) || prompt.phase === "resolved" || prompt.phase === "accepting") continue;
      if (askedAt <= prompt.shownAt) continue;
      // Gone after an Accept from here that got no answer: it was most likely that Accept.
      if (prompt.unconfirmedAt != null) toast(unconfirmedAcceptToast(tableLabel(prompt)));
      remove(prompt.id);
    }
  }, [list, remove, show, updateSkew, toast]);

  // ---- reconnect: whatever arrived while the socket was down ----
  const lastStatus = useRef(wsStatus);
  useEffect(() => {
    if (wsStatus === "connected" && lastStatus.current !== "connected" && !forbiddenRef.current) {
      void queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
    }
    lastStatus.current = wsStatus;
  }, [wsStatus, queryClient]);

  // ---- past the deadline: ask again, and in the end let go; an Accept with no answer: stop waiting ----
  useEffect(() => {
    let recheck = false;
    for (const prompt of prompts) {
      if (prompt.phase === "accepting") {
        if (prompt.acceptingSince != null && now - prompt.acceptingSince >= ACCEPT_GIVE_UP_MS) {
          dispatch({
            type: "update",
            id: prompt.id,
            patch: {
              phase: prompt.resumePhase ?? "open",
              acceptingSince: undefined,
              error: ACCEPT_UNKNOWN_MESSAGE,
              unconfirmedAt: prompt.acceptingSince,
            },
          });
          recheck = true;
        }
        continue;
      }
      if (prompt.phase !== "open") continue;
      const overdue = -(prompt.deadlineAt - (now + skewMs));
      if (overdue >= GIVE_UP_AFTER_MS) {
        remove(prompt.id);
      } else if (overdue >= RECHECK_AFTER_MS && !prompt.rechecked) {
        dispatch({ type: "update", id: prompt.id, patch: { rechecked: true } });
        recheck = true;
      }
    }
    if (recheck) void queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
  }, [now, prompts, skewMs, queryClient, remove]);

  const handleAccept = useCallback(
    async (prompt: AcceptPrompt) => {
      if (prompt.phase === "accepting" || prompt.phase === "resolved") return;
      const resumePhase = prompt.phase === "attention" ? "attention" : "open";
      const startedAt = Date.now();
      if (focusIsInPrompts()) acceptsHoldingFocus.current.set(prompt.id, lastInput.current === "keyboard");
      dispatch({
        type: "update",
        id: prompt.id,
        patch: { phase: "accepting", error: null, acceptingSince: startedAt, resumePhase },
      });
      try {
        const { result, printing } = await acceptAndPrint(prompt.id);
        refocusAfter(prompt.id);
        remove(prompt.id);
        setAnnouncement(`${tableLabel(prompt)}: accepted`);
        toast(acceptedToast(result.pending_order?.table_number ?? prompt.tableNumber, await printing));
      } catch (error) {
        const resolvedBy = alreadyResolved(error);
        const settle = (outcome: string) => {
          refocusAfter(prompt.id);
          resolve(prompt.id, outcome);
        };
        if (resolvedBy) return settle(resolvedLabel(resolvedBy, myProfileId()));
        if (error instanceof ApiError && error.code === "SESSION_ENDED") return settle("Table closed");
        if (error instanceof ApiError && error.status === 404) return settle(ORDER_REMOVED_LABEL);
        acceptsHoldingFocus.current.delete(prompt.id);
        if (isForbiddenError(error)) {
          setForbidden(true);
          toast({ title: "You can't accept QR orders", description: "Sign in again if your role changed.", variant: "destructive" });
          return;
        }
        // Still waiting on this Accept (not given up on, not resolved meanwhile): say why it failed.
        const current = promptsRef.current.find((p) => p.id === prompt.id);
        if (!current || current.phase !== "accepting" || current.acceptingSince !== startedAt) return;
        dispatch({
          type: "update",
          id: prompt.id,
          patch: {
            phase: current.resumePhase ?? resumePhase,
            acceptingSince: undefined,
            error: acceptErrorMessage(error),
            // No answer: it may have gone through. If the list then drops it, say so.
            ...(isNetworkError(error) ? { unconfirmedAt: startedAt } : {}),
          },
        });
        if (isNetworkError(error)) void queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
      }
    },
    [acceptAndPrint, focusIsInPrompts, queryClient, refocusAfter, remove, resolve, toast],
  );

  const handleDismiss = useCallback((prompt: AcceptPrompt) => remove(prompt.id), [remove]);
  const handleReview = useCallback(() => setReviewOpen(true), []);
  const handleMinimize = useCallback(() => setMinimized(true), []);

  const counting = useMemo(() => prompts.filter((p) => p.phase !== "resolved"), [prompts]);
  const soonest = counting[0];
  // A Pending QR Orders drawer open: it lists these orders, and the prompts would cover it.
  const panelOpen = useQRPendingPanelOpen();
  const visible = !forbidden && prompts.length > 0 && !panelOpen;

  const pillSeconds = soonest ? secondsLeft(remainingMs(soonest.deadlineAt, now, skewMs)) : 0;
  const pillLabel =
    counting.length > 1 ? `${counting.length} QR orders` : soonest ? tableLabel(soonest) : "QR order";

  return (
    <>
      {visible && (
        // A branch of every open dialog or sheet: tapping a prompt (Accept, Minimize)
        // doesn't close the cart sheet or a dialog underneath, as toasts don't.
        <DismissableLayerBranch
          ref={regionRef}
          role="region"
          aria-label="QR orders waiting"
          data-testid="qr-accept-prompts"
          className="pointer-events-none fixed inset-x-3 top-[calc(env(safe-area-inset-top)+4rem)] z-[60] flex flex-col items-stretch gap-2 sm:left-auto sm:right-4 sm:w-[23rem] xl:top-[4.5rem]"
        >
          {minimized ? (
            <button
              type="button"
              onClick={() => setMinimized(false)}
              aria-label={`Show QR orders: ${pillLabel}`}
              data-testid="qr-accept-pill"
              className="pointer-events-auto press ml-auto flex h-11 items-center gap-2 rounded-full bg-ink pl-4 pr-3 text-sm font-semibold text-white elev-1 animate-in fade-in-0 duration-200"
            >
              <BellRing aria-hidden="true" className="h-4 w-4" />
              <span>{pillLabel}</span>
              {soonest && soonest.phase !== "attention" && (
                <span className="font-mono tabular">· {pillSeconds > 0 ? `${pillSeconds}s` : "Auto-accepting…"}</span>
              )}
              <ChevronDown aria-hidden="true" className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex max-h-[calc(100dvh-5.5rem)] flex-col gap-2 overflow-y-auto overscroll-contain">
              {prompts.map((prompt) => (
                <QRAcceptPromptCard
                  key={prompt.id}
                  prompt={prompt}
                  now={now}
                  skewMs={skewMs}
                  canReview={canReview}
                  onAccept={(p) => void handleAccept(p)}
                  onReview={handleReview}
                  onMinimize={handleMinimize}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          )}
        </DismissableLayerBranch>
      )}
      {/* Always there, so the first announcement isn't lost while the region mounts. */}
      <p className="sr-only" aria-live="assertive" aria-atomic="true" data-testid="qr-accept-announcer">
        {announcement}
      </p>
      {canReview && !forbidden && <QRPendingOrdersPanel open={reviewOpen} onOpenChange={setReviewOpen} />}
    </>
  );
}
